import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Chip,
    Grid,
    Modal,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    alpha,
  } from '@mui/material'
  import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
  import { useEffect, useMemo, useState } from 'react'
  import { getDashBoard } from '../../services/dashboard'
  import { getdaysTimeStamp, startAndEndTime } from '../../utils'
  import { DatePickerComponent } from '../billing-history'
  import { IDashBoardAnalytics } from '../../all-types'
  import { useAppSelector } from '../../redux-store/hooks'
  
  type TabValue = 'YESTERDAY' | 'TODAY' | 'WEEK' | 'MONTH'
  
  type ProductSalesCount = {
    productName: string
    productImage: string
    saleCount: number
  }
  
  const placeholderImg =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='150'>
         <rect width='100%' height='100%' fill='#f2f2f2'/>
         <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#999' font-family='Arial' font-size='14'>No image</text>
       </svg>`
    )
  
  const KitchenStats: React.FC = () => {
    const today = new Date()
    const { startTimeStamp, endTimeStamp } = startAndEndTime(today, today)
  
    const [data, setData] = useState<Partial<IDashBoardAnalytics>>({})
    const [selectedTab, setSelectedTab] = useState<TabValue>('TODAY')
    const [startTime, setStartTime] = useState<number>(startTimeStamp)
    const [endTime, setEndTime] = useState<number>(endTimeStamp)
    const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)
    const products = useAppSelector((state) => state.allproducts.data)
  
    const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
      setSelectedTab(newValue)
      const { startTimeStamp, endTimeStamp } = getdaysTimeStamp(newValue)
      setStartTime(startTimeStamp)
      setEndTime(endTimeStamp)
    }
  
    useEffect(() => {
      const getData = async () => {
        try {
          const response = await getDashBoard(startTime, endTime)
          setData(response?.data?.data)
        } catch (e) {
          console.error(e)
        }
      }
      getData()
    }, [startTime, endTime, products])
  
    const salesRows: ProductSalesCount[] = useMemo(() => {
      const rows: ProductSalesCount[] = []
      try {
        products?.forEach((p) => {
          const count = data?.salesByProduct?.[p.productId]
          if (count && count >= 1) {
            rows.push({
              productName: p.name,
              productImage: p.image || placeholderImg,
              saleCount: count,
            })
          }
        })
        return rows.sort((a, b) => (b.saleCount ?? 0) - (a.saleCount ?? 0))
      } catch (e) {
        console.error(e)
        return []
      }
    }, [products, data?.salesByProduct])
  
    const totalUnits = useMemo(
      () => salesRows.reduce((acc, cur) => acc + (cur.saleCount || 0), 0),
      [salesRows]
    )
  
    return (
      <Box px={3} py={1}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h5" fontWeight={700}>Kitchen Stats</Typography>
            {salesRows.length > 0 && (
              <Chip
                label={`Total items sold: ${totalUnits.toLocaleString('en-IN')}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
  
          {/* Date picker + Tabs */}
          <Box display="flex" gap={2} alignItems="center">
            <Box onClick={() => setOpenDatePicker(true)} sx={{ cursor: 'pointer' }} aria-label="Choose date range">
              <CalendarMonthIcon fontSize="large" />
            </Box>
            <Tabs
              indicatorColor="primary"
              value={selectedTab}
              onChange={handleTabChange}
              sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <Tab value="YESTERDAY" label="Yesterday" sx={{ textTransform: 'none' }} />
              <Tab value="TODAY" label="Today" sx={{ textTransform: 'none' }} />
              <Tab value="WEEK" label="Week" sx={{ textTransform: 'none' }} />
              <Tab value="MONTH" label="Month" sx={{ textTransform: 'none' }} />
            </Tabs>
          </Box>
        </Box>
  
        {/* TILES: Beautiful, clear product tiles */}
        <Grid container spacing={2} mt={2}>
          {salesRows.map((p, idx) => (
            <Grid item key={`${p.productName}-${idx}`} xs={12} sm={6} md={4} lg={3} xl={2}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'transform 120ms ease, box-shadow 120ms ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 6,
                  },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardActionArea sx={{ height: '100%' }}>
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="150"
                      image={p.productImage || placeholderImg}
                      alt={p.productName}
                      onError={(e) => ((e.target as HTMLImageElement).src = placeholderImg)}
                      loading="lazy"
                      sx={{ objectFit: 'contain', bgcolor: (t) => alpha(t.palette.primary.light, 0.08) }}
                    />
                    {/* Rank badge for top items */}
                    {idx < 3 && (
                      <Chip
                        label={`#${idx + 1}`}
                        size="small"
                        color={idx === 0 ? 'success' : idx === 1 ? 'warning' : 'info'}
                        sx={{ position: 'absolute', top: 8, left: 8, borderRadius: 1.5 }}
                      />
                    )}
                  </Box>
  
                  <CardContent sx={{ display: 'grid', gap: 0.5 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={p.productName}
                    >
                      {p.productName}
                    </Typography>
  
                    <Tooltip title="Units sold in selected period">
                      <Typography
                        variant="h3"
                        component="div"
                        sx={{
                          lineHeight: 1.1,
                          fontWeight: 800,
                          letterSpacing: 0.5,
                          // responsive big number
                          fontSize: { xs: '2rem', sm: '2.25rem', md: '2.5rem' },
                        }}
                      >
                        {p.saleCount.toLocaleString('en-IN')}
                      </Typography>
                    </Tooltip>
  
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      items sold
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
  
          {salesRows.length === 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 3,
                  bgcolor: (t) => alpha(t.palette.info.light, 0.12),
                  border: (t) => `1px dashed ${alpha(t.palette.info.main, 0.4)}`,
                }}
              >
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  No sales found for the selected period
                </Typography>
                <Typography color="text.secondary">
                  Try adjusting the date range or check back later.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
  
        {/* Date Picker Modal */}
        <Modal
          open={openDatePicker}
          onClose={() => setOpenDatePicker(false)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              minWidth: 300,
              outline: 'none',
            }}
          >
            <DatePickerComponent
              selectRange={(start, end) => {
                setStartTime(start)
                setEndTime(end)
                setOpenDatePicker(false)
              }}
              onClose={() => setOpenDatePicker(false)}
            />
          </Box>
        </Modal>
      </Box>
    )
  }
  
  export { KitchenStats }
  